import { cn } from "@/lib/utils";
import { CheckIcon } from "@phosphor-icons/react";
import { useId, type KeyboardEvent, type Ref } from "react";
import type { ChoiceItem } from "./types";

/**
 * One radio of the list. Its accessible name is the title alone; the rest of the
 * row (facts, description, tags, aside) is its description, so a screen reader
 * walking the list hears "Léa, radio, 2 of 12" before the details.
 */
export function ChoiceRow({
  item,
  checked,
  tabIndex,
  buttonRef,
  onSelect,
  onFocus,
  onKeyDown,
}: {
  item: ChoiceItem;
  checked: boolean;
  tabIndex: number;
  buttonRef: Ref<HTMLButtonElement>;
  onSelect: () => void;
  onFocus: () => void;
  onKeyDown: (event: KeyboardEvent) => void;
}) {
  const id = useId();
  const part = (name: string) => `${id}-${name}`;
  const describedBy = [
    item.meta && part("meta"),
    item.description && part("description"),
    !!item.tags?.length && part("tags"),
    item.aside && part("aside"),
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={cn(
        "flex items-center gap-1 rounded-md pr-1 transition-colors",
        checked ? "bg-primary/10" : "hover:bg-slate-500/5",
      )}
    >
      <button
        ref={buttonRef}
        type="button"
        role="radio"
        aria-checked={checked}
        aria-labelledby={part("title")}
        aria-describedby={describedBy || undefined}
        tabIndex={tabIndex}
        onClick={onSelect}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        className="focus-visible:ring-ring flex min-w-0 flex-1 items-start gap-3 rounded-md px-3 py-2.5 text-left outline-none focus-visible:ring-2"
      >
        <span
          className={cn(
            "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
            item.leading && "self-center",
            checked
              ? "border-primary bg-primary text-primary-foreground"
              : "border-input",
          )}
        >
          {checked && <CheckIcon weight="bold" className="h-3 w-3" />}
        </span>
        {item.leading}
        <span className="min-w-0 flex-1 space-y-1">
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span id={part("title")} className="text-sm font-medium">
              {item.title}
            </span>
            {item.meta && (
              <span id={part("meta")} className="text-muted-foreground text-xs">
                {item.meta}
              </span>
            )}
          </span>
          {item.description && (
            <span
              id={part("description")}
              className="text-muted-foreground block text-xs"
            >
              {item.description}
            </span>
          )}
          {!!item.tags?.length && (
            <span id={part("tags")} className="flex flex-wrap gap-1">
              {item.tags.map((tag, i) => (
                <span
                  key={i}
                  className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[11px] leading-none"
                >
                  {tag}
                </span>
              ))}
            </span>
          )}
        </span>
        {item.aside && (
          <span
            id={part("aside")}
            className="text-muted-foreground hidden shrink-0 flex-col items-end gap-0.5 text-xs sm:flex"
          >
            {item.aside}
          </span>
        )}
      </button>
      {item.action?.({ tabIndex })}
    </div>
  );
}
